// src/pages/ManagementDashboard/ManagementDashboard.tsx
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import './ManagementDashboard.css';
import { LogOut } from 'lucide-react';
import { logout } from '../../services/authService';

import DashboardHome from '../../components/DashboardHome';
import ChannelSettings from '../../components/ChannelSettings';
import AccountSettings from '../../components/AccountSettings';
import TwoFASettings from '../../components/TwoFASettings';
import SubscriptionManagement from '../../components/SubscriptionManagement';
import DocumentManagement from '../../components/DocumentManagement';
import ConversationHistory from '../../components/ConversationHistory';
import LoadingIndicator from '../../components/LoadingIndicator';
import NotificationBar from '../../components/NotificationBar';

import { getCurrentUser } from '../../services/userService';
import { NotificationInfo } from '../../types/NotificationInfo';

// ────────────────────────────────────────────────────────────
// Lazy-loaded pages
// ────────────────────────────────────────────────────────────
const ContactPage = lazy(() => import('../../components/ContactPage'));

// ────────────────────────────────────────────────────────────
// Tabs
// ────────────────────────────────────────────────────────────
export enum TabKey {
  HOME = 'HOME',
  CHANNEL = 'CHANNEL',
  ACCOUNT = 'ACCOUNT',
  TWO_FA = 'TWO_FA',
  SUBSCRIPTION = 'SUBSCRIPTION',
  DOCUMENT = 'DOCUMENT',
  CONVERSATION = 'CONVERSATION',
  CONTACT = 'CONTACT',
}

const ManagementDashboard: React.FC = () => {
  const navigate = useNavigate();
  /* ───────── state ───────── */
  const storedTab = localStorage.getItem('selectedTab') as TabKey | null;
  const [currentTab, setCurrentTab] = useState<TabKey>(
    storedTab || TabKey.CHANNEL
  );
  const [notification, setNotification] = useState<NotificationInfo | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<any>(null);

  /* ───────── helpers ───────── */
  const refreshUser = useCallback(async () => {
    try {
      const res = await getCurrentUser();
      setUser(res.data);
    } catch {
      setNotification({
        message: 'ユーザー情報の更新に失敗しました。',
        bgColor: '#dc3545',
        textColor: '#fff',
      });
    }
  }, []);

  /* ───────── lifecycle ───────── */
  useEffect(() => {
    setLoading(true);
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  /* ───────── handlers ───────── */
  const handleTabChange = (tab: TabKey) => {
    setCurrentTab(tab);
    localStorage.setItem('selectedTab', tab);

    const navToggle = document.getElementById('nav-toggle') as HTMLInputElement | null;
    if (navToggle) {
      navToggle.checked = false;
    }

  };

  const handleLogout = () => {
    if (window.confirm('本当にログアウトしますか？')) {
      logout();
      navigate('/login');
    }
  };


  /* ───────── early return: official 未登録 ───────── */
  if (user && !user.is_official) {
    return (
      <div className="page-wrapper">
        <header className="header register-header">
          <h1>公式アカウント設定</h1>
          <p>
            公式アカウントとして登録するために、以下の LINE
            チャネル情報を入力してください。
            <br /><a href="/instruction" target="_blank" rel="noopener noreferrer">設定方法はこちらをご覧ください。</a>
          </p>
        </header>

        {loading && <LoadingIndicator isFullPage />}

        {notification && (
          <NotificationBar
            {...notification}
            isOpen
            onClose={() => setNotification(null)}
          />
        )}

        <div className="content-area">
          <ChannelSettings
            user={user}
            setNotification={setNotification}
            refreshUser={refreshUser}
          />
        </div>
        <div className="contact-support">
          <p>
            アカウントの設定に関するご質問は、下記よりお問い合わせください。
            <br />
            <a href="/contact" target="_blank" rel="noreferrer">お問い合わせ</a>
          </p>
        </div>
      </div>
    );
  }

  /* ───────── regular dashboard ───────── */
  return (
    <div className="page-wrapper">
      <header className="header">
        <h1><img src="/assets/images/logo_w.png" alt="autoChat AI" /></h1>
        <button
          type="button"
          className="logout-button pc_disp_db"
          aria-label="ログアウト"
          onClick={handleLogout}
        >
          <LogOut />
        </button>
      </header>

      <div className="main">
        {/* ───── tab bar ───── */}
        <input type="checkbox" id="nav-toggle" className="nav-toggle" placeholder='checkbox' />
        {/* ハンバーガー／バツアイコン */}
        <label htmlFor="nav-toggle" className="nav-icon">
          <span></span>
          <span></span>
          <span></span>
        </label>
        <nav className="tab-bar">
          {(
            [
              [TabKey.HOME, 'ホーム'],
              [TabKey.CHANNEL, 'LINE チャネル・設定'],
              [TabKey.ACCOUNT, 'アカウント管理'],
              [TabKey.TWO_FA, '2 段階認証'],
              [TabKey.SUBSCRIPTION, 'サブスクリプション管理'],
              [TabKey.DOCUMENT, 'ドキュメント管理'],
              [TabKey.CONVERSATION, 'チャット履歴'],
              [TabKey.CONTACT, 'お問い合わせ'],
            ] as const
          ).map(([key, label]) => (
            <div
              key={key}
              role="button"
              tabIndex={0}
              className={currentTab === key ? 'active' : undefined}
              onClick={() => handleTabChange(key)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && handleTabChange(key)
              }
            >
              {label}
            </div>
          ))}
        </nav>

        {notification && (
          <NotificationBar
            {...notification}
            isOpen
            onClose={() => setNotification(null)}
          />
        )}

        {/* ───── content ───── */}
        <div className="content-area">
          {currentTab === TabKey.HOME && <DashboardHome user={user} />}

          {currentTab === TabKey.CHANNEL && (
            <ChannelSettings
              user={user}
              setNotification={setNotification}
              refreshUser={refreshUser}
            />
          )}

          {currentTab === TabKey.ACCOUNT && (
            <AccountSettings
              user={user}
              setUser={setUser}
              setNotification={setNotification}
            />
          )}

          {currentTab === TabKey.TWO_FA && (
            <TwoFASettings setNotification={setNotification} />
          )}

          {currentTab === TabKey.SUBSCRIPTION && (
            <SubscriptionManagement
              user={user}
              setNotification={setNotification}
              refreshUser={refreshUser}
            />
          )}

          {currentTab === TabKey.DOCUMENT && (
            <DocumentManagement user={user} setNotification={setNotification} />
          )}

          {currentTab === TabKey.CONVERSATION && (
            <ConversationHistory setNotification={setNotification} />
          )}

          {currentTab === TabKey.CONTACT && (
            <Suspense>
              <ContactPage />
            </Suspense>
          )}
        </div>
      </div>
      <footer className="footer">
        <div className='footer-logo'>
          <img src="/assets/images/logo.png" alt="autoChat AI" />
        </div>
        <div className="footer-links">
          <span>© 2025 autoChat AI Inc.</span> &nbsp;|&nbsp;
          <a href="/privacy-policy">プライバシーポリシー</a> &nbsp;|&nbsp;
          <a href="/terms-of-service">利用規約</a> &nbsp;|&nbsp;
          <a href="/tokushoho">特定商取引法に基づく表記</a>
        </div>
      </footer>
    </div>
  );
};

export default ManagementDashboard;
